import type { PublicSiteId } from './publicSite'
import { getPublicSiteId, normalizeHostname } from './publicSite'

const LOCAL_DEV_HOSTS = new Set(['localhost', '127.0.0.1', '::1'])

/**
 * Local/preview only: `?cwApi=https://host/api` points ConstructionWalk at a
 * non-prod API without a rebuild. Ignored on cswalk.com.
 */
export function constructionApiOverride(
  hostname?: string,
  search?: string,
): string | undefined {
  const host =
    hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : '')
  if (!LOCAL_DEV_HOSTS.has(normalizeHostname(host))) return undefined
  const query =
    search ??
    (typeof window !== 'undefined' ? window.location.search : '')
  const raw = new URLSearchParams(query).get('cwApi')
  if (!raw) return undefined
  try {
    const url = new URL(raw)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined
    return url.href.replace(/\/$/, '')
  } catch {
    return undefined
  }
}

/**
 * Resolve API base URL from hostname so one SPA build can hit SiteWalk, PatrolWalk, or ConstructionWalk.
 * - storagewalk.com → REACT_APP_API_URL (api.sitewalker.org)
 * - patrolwalk.com → REACT_APP_PATROL_API_URL (api.patrolwalk.com)
 * - cswalk.com → REACT_APP_CONSTRUCTION_API_URL (ConstructionWalk API)
 * - localhost → API URL for the product selected via ?product= query param, or Storage default
 *   ConstructionWalk on localhost also honors `?cwApi=` or REACT_APP_CONSTRUCTION_API_URL
 */
export function getApiBaseUrl(hostname?: string, search?: string): string {
  const storageUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001'
  const patrolUrl =
    process.env.REACT_APP_PATROL_API_URL || 'https://api.patrolwalk.com/api'
  const constructionUrl =
    process.env.REACT_APP_CONSTRUCTION_API_URL || 'https://api.cswalk.com/api'

  const host =
    hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : '')
  const normalized = normalizeHostname(host)
  const siteId: PublicSiteId = getPublicSiteId(host)
  const cwOverride = constructionApiOverride(normalized, search)

  if (LOCAL_DEV_HOSTS.has(normalized)) {
    switch (siteId) {
      case 'security-walk':
        return process.env.REACT_APP_PATROL_API_URL || storageUrl
      case 'construction-walk':
        return cwOverride || process.env.REACT_APP_CONSTRUCTION_API_URL || storageUrl
      default:
        return storageUrl
    }
  }

  switch (siteId) {
    case 'security-walk':
      return patrolUrl
    case 'construction-walk':
      return constructionUrl
    default:
      return storageUrl
  }
}
